import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, CATEGORY_COLORS, formatSeconds, competitionResultLabel } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { UnclaimedData } from "@/components/UnclaimedData";
import { useParentAuth } from "@/context/ParentAuthContext";
import { toast } from "sonner";
import { Calendar, MapPin, Plus, ArrowRight, Bell, RefreshCw, Lock } from "lucide-react";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

export default function ParentOverview() {
  const { CATEGORY_LABEL } = useCategories();
  const { parent } = useParentAuth();
  const [cubers, setCubers] = useState([]);
  const [children, setChildren] = useState([]);
  const [events, setEvents] = useState([]);
  const [privateEvents, setPrivateEvents] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [unclaimed, setUnclaimed] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState("");
  const loadAccount = async (sync = true) => {
    try {
      if (sync) await api.post("/parent/sync-my-cubers");
      const [entries, kids, pending] = await Promise.all([api.get("/parent/my-competitors"), api.get("/parent/children"), api.get("/parent/unclaimed-data")]);
      setCubers(entries.data || []); setChildren(kids.data || []); setUnclaimed(pending.data.groups || []); setLoadError("");
      return pending.data.groups || [];
    } catch { setLoadError("Could not load your family's data. Please try again."); }
    finally { setLoadingData(false); }
  };
  useEffect(() => {
    loadAccount();
    api.get("/events/public?kind=competition").then((r) => setEvents(r.data || [])).catch(() => {});
    api.get("/parent/private-events").then((r) => setPrivateEvents(r.data || [])).catch(() => {});
    // Reload when the signed-in account changes, never reuse another parent's data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent?.user_id]);

  const syncPast = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/parent/sync-my-cubers");
      const groups = await loadAccount(false);
      if (data.linked > 0) toast.success(`Automatically linked ${data.linked} matching ${data.linked === 1 ? "entry" : "entries"}.`);
      else if (groups?.length) toast.message("Your unclaimed data is ready to review above.");
      else toast.message("Your competition data is up to date.");
    } catch { toast.error("Sync failed. Please try again."); }
    finally { setSyncing(false); }
  };

  return (
    <div>
      <span className="text-xs uppercase tracking-wider font-semibold text-[#FF9F1C]">Overview</span>
      <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tight">Hi, {parent?.name || "there"}.</h1>
      <p className="mt-2 text-[#7A7D8C]">Manage your cubers, register for competitions, and view results here.</p>

      {loadingData && <p className="mt-6 text-sm text-[#7A7D8C]" data-testid="parent-data-loading">Checking for your competition data…</p>}
      {loadError && <div role="alert" className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="parent-data-error">{loadError}<button onClick={() => loadAccount()} className="ml-2 underline font-semibold" data-testid="parent-data-retry">Try again</button></div>}
      <UnclaimedData groups={unclaimed} children={children} onClaimed={() => loadAccount()} />

      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        <Kpi label="My cubers" value={children.length} tid="kpi-children" />
        <Kpi label="Registered entries" value={cubers.length} tid="kpi-registered" />
        <Kpi label="Upcoming competitions" value={events.length + privateEvents.length} tid="kpi-events" />
      </div>

      {privateEvents.length > 0 && (
        <div className="mt-8 bg-white rounded-2xl border-2 border-[#FDE74C] p-6" data-testid="overview-private-events">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-[#B36906]" />
            <h2 className="font-display font-bold text-lg">By invitation</h2>
            <span className="text-[10px] uppercase tracking-widest font-black text-[#B36906] bg-[#FDE74C]/60 rounded-full px-2 py-0.5">Private</span>
          </div>
          <ul className="grid md:grid-cols-2 gap-3">
            {privateEvents.map((e) => (
              <li key={e.id} className="bg-[#FDFFFC] rounded-xl border border-slate-100 px-4 py-3" data-testid={`private-event-${e.id}`}>
                <div className="font-semibold">{e.title}</div>
                <div className="mt-1 flex flex-wrap gap-4 text-xs text-[#7A7D8C]">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(e.start_date)}</span>
                  {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>}
                  {e.registration_fee != null && <span className="font-semibold text-[#B36906]">${Number(e.registration_fee).toFixed(2)}</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">Upcoming competitions</h2>
            {events.length > 0 && <Link to="/parent/register" className="text-sm text-[#FF9F1C] font-semibold">Register →</Link>}
          </div>
          {events.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-slate-200 px-4 py-6 text-sm text-[#7A7D8C]" data-testid="overview-no-events">
              <Bell className="w-4 h-4 inline -mt-0.5 mr-1 text-[#FF9F1C]" /> Nothing on the calendar yet — we'll email you when the next one drops (if enabled in <Link to="/parent/settings" className="text-[#FF9F1C] font-semibold">Settings</Link>).
            </div>
          ) : (
            <ul className="space-y-3">
              {events.slice(0, 4).map((e) => (
                <li key={e.id} className="bg-[#FDFFFC] rounded-xl border border-slate-100 px-4 py-3">
                  <div className="font-semibold">{e.title}</div>
                  <div className="mt-1 flex flex-wrap gap-4 text-xs text-[#7A7D8C]">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(e.start_date)}</span>
                    {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>}
                    {e.registration_fee != null && <span className="font-semibold text-[#B36906]">${Number(e.registration_fee).toFixed(2)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">Your cubers</h2>
            <Link to="/parent/cubers" className="text-sm text-[#FF9F1C] font-semibold">Manage →</Link>
          </div>
          {children.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-slate-200 px-4 py-6 text-sm text-[#7A7D8C]" data-testid="overview-no-children">
              Add your first cuber to speed up registration next time. <Link to="/parent/cubers" className="text-[#FF9F1C] font-semibold">Add now</Link>.
            </div>
          ) : (
            <ul className="space-y-2">
              {children.slice(0, 4).map((c) => (
                <li key={c.id} className="flex items-center justify-between bg-[#FDFFFC] rounded-xl border border-slate-100 px-4 py-3">
                  <div>
                    <div className="font-semibold"><Link to={`/parent/cubers/${c.id}`} data-testid={`overview-child-${c.id}`} className="hover:text-[#B36906]">{c.full_name}</Link></div>
                    {c.current_school && <div className="text-xs text-[#7A7D8C]">{c.current_school}</div>}
                  </div>
                  {c.shirt_size && <span className="text-[11px] uppercase font-bold text-[#7A5F00] bg-[#FDE74C]/40 px-2 py-0.5 rounded-full">{c.shirt_size}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8 bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-display font-bold text-lg">Recent results</h2>
          <button onClick={syncPast} disabled={syncing} data-testid="sync-past-btn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 text-xs font-semibold hover:border-[#FF9F1C] disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync past entries"}
          </button>
        </div>
        {cubers.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-slate-200 px-4 py-6 text-sm text-[#7A7D8C]" data-testid="overview-no-results">
            No results to show yet. Published competition times and rankings will appear here.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {cubers.map((r) => (
              <div key={r.id} className="bg-[#FDFFFC] rounded-xl border border-slate-100 p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{r.full_name}</div>
                  {r.assigned_category && <span className={`text-[11px] font-bold border px-2 py-0.5 rounded-full ${CATEGORY_COLORS[r.assigned_category] || ""}`}>{CATEGORY_LABEL[r.assigned_category]}</span>}
                </div>
                {r.event && <div className="mt-1 text-xs text-[#7A7D8C]">{r.event.title}</div>}
                <div className="mt-3 flex gap-3 text-xs">
                  <span data-testid={`overview-prelim-${r.id}`}>Timing Solve: <b>{formatSeconds(r.prelim_average)}</b></span>
                  <span data-testid={`overview-result-${r.id}`}>{competitionResultLabel(r)}: <b>{r.official_dnf ? "DNF" : formatSeconds(r.official_average)}</b></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {events.length > 0 && children.length > 0 && (
        <div className="mt-8 rounded-2xl bg-[#FF9F1C] p-6 md:p-8 text-white flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-display font-black text-2xl">Register a cuber</h3>
            <p className="text-white/90 mt-1">Pick a child, pick an event, pay in a tap.</p>
          </div>
          <Link to="/parent/register" data-testid="cta-register" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#B36906] font-bold"><Plus className="w-4 h-4" /> Register now <ArrowRight className="w-4 h-4" /></Link>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={tid}>
      <div className="text-xs uppercase tracking-wider text-[#7A7D8C] font-semibold">{label}</div>
      <div className="mt-2 font-display font-black text-3xl">{value}</div>
    </div>
  );
}
